import { Admin, CustomRoutes, Resource } from 'react-admin';
import { Route } from 'react-router-dom';
import Dashboard from './dashboard/Dashboard';
import { dataProvider } from './dataProvider';
import { authProvider } from './auth/authProvider';
import LoginPage from './auth/LoginPage';
import { AppLayout } from './layout/AppLayout';
import { StoreList } from './stores/StoreList';
import { OfflineStoreList } from './offlineStores/OfflineStoreList';
import {
  OfflineStoreCreate,
  OfflineStoreEdit,
} from './offlineStores/OfflineStoreForm';
import { StoreShow } from './stores/StoreShow';
import { StoreEdit } from './stores/StoreEdit';
import { UserList } from './users/UserList';
import { UserShow } from './users/UserShow';
import { UserEdit } from './users/UserEdit';
import { CashbackList } from './cashbacks/CashbackList';
import { CashbackShow } from './cashbacks/CashbackShow';
import { CashoutList } from './cashouts/CashoutList';
import { CashoutShow } from './cashouts/CashoutShow';
import { CharityList } from './charities/CharityList';
import { CharityShow } from './charities/CharityShow';
import { CharityCreate } from './charities/CharityCreate';
import { CharityEdit } from './charities/CharityEdit';
import { CategoryList } from './categories/CategoryList';
import { CategoryShow } from './categories/CategoryShow';
import { CategoryEdit } from './categories/CategoryEdit';
import { NetworkList } from './networks/NetworkList';
import { NetworkShow } from './networks/NetworkShow';
import { NetworkEdit } from './networks/NetworkEdit';
import { Earnings } from './earnings/Earnings';
import { StorePerformance } from './storePerformance/StorePerformance';
import { ExitClicks } from './exitClicks/ExitClicks';
import { Subscriptions } from './subscriptions/Subscriptions';
import { Referrals } from './referrals/Referrals';
import { ManualAttribution } from './attribution/ManualAttribution';
import { FraudQueue } from './fraud/FraudQueue';
import { Suspensions } from './suspensions/Suspensions';
import { BanEvasion } from './banEvasion/BanEvasion';
import { Chargebacks } from './chargebacks/Chargebacks';
import { Adjustments } from './adjustments/Adjustments';
import { TicketInbox } from './tickets/TicketInbox';
import { TrackingDiagnostics } from './tracking/TrackingDiagnostics';
import { ViewAsUser } from './impersonation/ViewAsUser';
import { DsarQueue } from './dsar/DsarQueue';
import { ConsentRecords } from './consent/ConsentRecords';
import { SanctionsQueue } from './sanctions/SanctionsQueue';
import { TaxReport } from './tax/TaxReport';
import { ErasureQueue } from './erasure/ErasureQueue';
import { PromoCodeList } from './promoCodes/PromoCodeList';
import { PromoCodeCreate } from './promoCodes/PromoCodeCreate';
import { PromoCodeEdit } from './promoCodes/PromoCodeEdit';
import { PlacementList } from './placements/PlacementList';
import { PlacementCreate } from './placements/PlacementCreate';
import { PlacementEdit } from './placements/PlacementEdit';
import { CannedResponseList } from './cannedResponses/CannedResponseList';
import { CannedResponseCreate } from './cannedResponses/CannedResponseCreate';
import { CannedResponseEdit } from './cannedResponses/CannedResponseEdit';
import { StoreTagList } from './storeTags/StoreTagList';
import { StoreTagCreate } from './storeTags/StoreTagCreate';
import { StoreTagEdit } from './storeTags/StoreTagEdit';
import { RateRuleList } from './rateRules/RateRuleList';
import { RateRuleCreate, RateRuleEdit } from './rateRules/RateRuleForm';
import { RuleCalendar } from './rateRules/RuleCalendar';
import { PageSchemaList } from './pageSchema/PageSchemaList';
import { PageSchemaCreate, PageSchemaEdit } from './pageSchema/PageSchemaForm';
import { CmsPageList } from './cms/CmsPageList';
import { CmsPageCreate, CmsPageEdit } from './cms/CmsPageForm';
import { BlogPostList } from './blog/BlogPostList';
import { BlogPostCreate, BlogPostEdit } from './blog/BlogPostForm';
import { BlogCategoryList } from './blog/BlogCategoryList';
import { BlogCategoryCreate } from './blog/BlogCategoryCreate';
import { BlogCategoryEdit } from './blog/BlogCategoryEdit';
import { TestimonialList } from './testimonials/TestimonialList';
import {
  TestimonialCreate,
  TestimonialEdit,
} from './testimonials/TestimonialForm';
import { SeoRuleList } from './seo/SeoRuleList';
import { SeoRuleCreate, SeoRuleEdit } from './seo/SeoRuleForm';
import { SliderManager } from './sliders/SliderManager';
import { SliderCreate, SliderEdit } from './sliders/SliderForm';
import { MenuManager } from './menu/MenuManager';
import { MenuItemCreate, MenuItemEdit } from './menu/MenuItemForm';
import { SettingsPage } from './settings/SettingsPage';
import { ApiDocs } from './settings/ApiDocs';
import { EmailTemplateList } from './emailTemplates/EmailTemplateList';
import { EmailTemplateEdit } from './emailTemplates/EmailTemplateForm';
import { ComplianceSettingsPage } from './compliance/ComplianceSettingsPage';
import { EventList } from './events/EventList';
import { EventShow } from './events/EventShow';
import { AlertConfigPage } from './alerts/AlertConfigPage';
import { AlertList } from './alerts/AlertList';
import { AlertShow } from './alerts/AlertShow';
import { AuditList } from './audit/AuditList';

/**
 * Root of the Cashblack admin panel.
 *
 * Auth (0.2): the Cognito `authProvider` gates the app; unauthenticated users
 * hit the custom `loginPage`. Access control (0.3): the custom `AppLayout`
 * provides region context + an access-gated menu; surfaces gate themselves with
 * `RequireAccess` (Stores: `stores:read`, Users: `users:read`, Cashbacks:
 * `cashbacks:read`, Cashouts: `cashouts:read`).
 *
 * The data provider is the in-memory mock (no backend yet), wrapped by the audit
 * floor (D-audit) so every mutation is recorded. Phase 1 read-only surfaces: the
 * Stores, Users, Cashbacks and Cashouts lists; the Audit list (super_admin-only).
 * Phase 2: Stores + Users gain detail (show) + edit (the first form mutations);
 * Cashbacks + Cashouts gain a detail with action-style status transitions
 * (confirm/reject, approve/reject — the admin AUTHORISES, a future engine
 * EXECUTES; D-payout-boundary). Charities gains full management — list / show /
 * CREATE (the first create) / edit — with deactivate-not-delete
 * (D-charity-softdelete); Categories gets a light list + edit (no create, no
 * tree editing; D-categories-light). Networks are read-only reference data
 * (list + detail; D-networks-readonly). All audited for free through the wrapper.
 * Phase 3 opens the analytical surfaces: Earnings (Reports > Earnings,
 * /earnings) is the first - a read-only per-entity margin view, so it adds no
 * mutations and the audit floor stays dormant (D-earnings).
 */
const App = () => (
  <Admin
    dataProvider={dataProvider}
    authProvider={authProvider}
    loginPage={LoginPage}
    dashboard={Dashboard}
    layout={AppLayout}
  >
    <Resource
      name="stores"
      list={StoreList}
      show={StoreShow}
      edit={StoreEdit}
    />
    <Resource name="users" list={UserList} show={UserShow} edit={UserEdit} />
    <Resource name="cashbacks" list={CashbackList} show={CashbackShow} />
    <Resource name="cashouts" list={CashoutList} show={CashoutShow} />
    <Resource
      name="charities"
      list={CharityList}
      show={CharityShow}
      create={CharityCreate}
      edit={CharityEdit}
    />
    <Resource
      name="categories"
      list={CategoryList}
      show={CategoryShow}
      edit={CategoryEdit}
    />
    {/* Affiliate networks: list + show are read-only reference data
        (D-networks-readonly); Settings S2 (D-settings-s2-networks) PROMOTES them
        with an EDIT (edit-only, no create/delete). The edit gates networks:write
        (super_admin-only; the editor holds no networks grant): editable name /
        params / per-market commission (forced reason) + a write-only credential
        (redacted); slug / storeCount / lastImportedAt stay read-only. */}
    <Resource
      name="affiliateNetworks"
      list={NetworkList}
      show={NetworkShow}
      edit={NetworkEdit}
    />
    {/* Promo codes (Phase 4): managed CRUD, Charities-shaped, deactivate-not-
        delete (no `remove`). Create is gated promo_codes:issue, Edit
        promo_codes:write (D-promo-codes). */}
    <Resource
      name="promoCodes"
      list={PromoCodeList}
      create={PromoCodeCreate}
      edit={PromoCodeEdit}
    />
    {/* Placements (Phase 4): managed CRUD, Charities-shaped, but ENDED-via-window
        not deactivated (no `active` flag, no `remove`). Internal merchandising, so
        the whole CRUD gates on placements:write (editor and up; D-placements). */}
    <Resource
      name="placements"
      list={PlacementList}
      create={PlacementCreate}
      edit={PlacementEdit}
    />
    {/* Canned responses (Phase 6, surface 2): a SHARED GLOBAL library of reusable
        response templates with variable substitution, plus macros (template + a
        capability-guarded ticket action). Charities-shaped managed CRUD, deactivate-
        not-delete (no `remove`). The whole management surface gates on
        canned_responses:write (super_admin); the support agent USES the library from a
        ticket via canned_responses:read. Copy-out only, never a ticket write
        (D-canned-responses). */}
    <Resource
      name="cannedResponses"
      list={CannedResponseList}
      create={CannedResponseCreate}
      edit={CannedResponseEdit}
    />
    {/* Store tags (Phase 8, D-multi-channel-editor): the first-class tag
        vocabulary the store editor assigns and rate rules eventually scope by.
        Managed create + edit (the KEY is immutable, rate rules reference it), NO
        delete. Reference data (not switcher-scoped). List gates stores:read,
        create/edit stores:write (editor and up). */}
    <Resource
      name="storeTags"
      list={StoreTagList}
      create={StoreTagCreate}
      edit={StoreTagEdit}
    />
    {/* Offline stores (Phase 8, D-offline-stores): brick-and-mortar / card-linked-offer
        VENUES, its OWN collection (one row per physical venue, the per-venue cardinality
        the brand/region/channel model cannot hold). The offline OB/CLO tracking axis, the
        CLO matching config (authoring only), a postcode/ZIP search filter. Gated
        stores:write to author; region-scoped, deactivate-not-delete. NOT the consumer map. */}
    <Resource
      name="offlineStores"
      list={OfflineStoreList}
      create={OfflineStoreCreate}
      edit={OfflineStoreEdit}
    />
    {/* Rate rules (Phase 8, surface 2, D-rate-rules): the rule-AUTHORING tool whose
        value is PREVIEWING the engine's output honestly. The admin authors rules
        (scope / effect / tier / market / priority / window); the backend evaluates
        them per click. The surface mirrors the contract resolver for its projection
        previews (the real shared resolver is the cross-repo seam) and never IS the
        engine. Authoring gates rate_rules:author; activating / pausing are distinct
        capabilities (the reversible going-live act). Role-gated to editor and
        super_admin. */}
    <Resource
      name="rateRules"
      list={RateRuleList}
      create={RateRuleCreate}
      edit={RateRuleEdit}
    />
    {/* Page schema editor (Phase 8, surface 3a, D-page-schema-editor-8a): the
        authoring CORE of the schema-driven block editor. DRAFTS ONLY: the drag-drop
        composer (dnd-kit) over the LOCKED block contract mirrored from
        @cashblack/contracts (the cross-repo swap seam, ../pageSchema/blocks.ts),
        per-block config and reference resolution by merchant slug / category slug. A
        draft saves only when every block is valid against the mirror; there is NO
        publish path (publish + validation + rollback are 8b, live preview 8c,
        scheduling 8d). Authoring gates pages:author; role-gated to editor and
        super_admin, operational market-scoped. */}
    <Resource
      name="pageSchemas"
      list={PageSchemaList}
      create={PageSchemaCreate}
      edit={PageSchemaEdit}
    />
    {/* Conventional rich-text CMS, sub-surface (a) pages core (D-cms-pages-core).
        Marketing DOCUMENTS authored as TipTap rich text, SEPARATE from the
        page-schema app screens (blocks). Simple draft/published status, cms:author
        to author and cms:publish to go live; role-gated to editor and super_admin,
        operational market-scoped (market is uk/us/ie or all). */}
    <Resource
      name="cmsPages"
      list={CmsPageList}
      create={CmsPageCreate}
      edit={CmsPageEdit}
    />
    {/* Conventional rich-text CMS, sub-surface (b) blog (D-cms-blog). REUSES the
        cmsPages rich-text body and lifecycle in a SEPARATE blogPosts collection (plus
        category / featured image / author / excerpt), and a managed blogCategories
        vocabulary cloned from storeTags (immutable key, no delete). cms:author to
        author, cms:publish to go live; role-gated to editor and super_admin. */}
    <Resource
      name="blogPosts"
      list={BlogPostList}
      create={BlogPostCreate}
      edit={BlogPostEdit}
    />
    <Resource
      name="blogCategories"
      list={BlogCategoryList}
      create={BlogCategoryCreate}
      edit={BlogCategoryEdit}
    />
    {/* Conventional rich-text CMS, sub-surface (c1) testimonials + SEO rules
        (D-cms-testimonials-seo). Two FLAT structured surfaces, NO rich text and NO
        publish lifecycle: a testimonial is the active/inactive toggle, an SEO rule keys
        to a FREE pageKey/path (not a FK). Both gated cms:author, role-gated to editor and
        super_admin, operational market-scoped. */}
    <Resource
      name="testimonials"
      list={TestimonialList}
      create={TestimonialCreate}
      edit={TestimonialEdit}
    />
    <Resource
      name="seoRules"
      list={SeoRuleList}
      create={SeoRuleCreate}
      edit={SeoRuleEdit}
    />
    {/* Conventional rich-text CMS, sub-surface (c2) sliders (D-cms-sliders). Marketing-site
        slides GROUPED BY PLACEMENT with a per-placement dnd-kit reorder, the active toggle,
        NO publish. SEPARATE from the merchant Placement collection and the app's
        carousel/banner blocks. Gated cms:author, role-gated to editor and super_admin. */}
    <Resource
      name="sliderSlides"
      list={SliderManager}
      create={SliderCreate}
      edit={SliderEdit}
    />
    {/* Conventional rich-text CMS, sub-surface (d) the navigation menu (D-cms-menu), the
        LAST CMS piece. A nested link TREE (adjacency list parentId + order) capped at 2
        levels, grouped by a free location key, the dnd-kit per-level reorder plus an
        explicit re-parent select, the active toggle, NO publish. With this the conventional
        CMS is COMPLETE. Gated cms:author, role-gated to editor and super_admin. */}
    <Resource
      name="menuItems"
      list={MenuManager}
      create={MenuItemCreate}
      edit={MenuItemEdit}
    />
    {/* Email templates (Phase 9, Settings S3, D-settings-s3-email-templates): the
        folded-in Phase 8 surface. A FIXED set of transactional templates, EDIT-ONLY
        (no create, no delete; a template key is a backend SES sender contract), one
        row per (templateKey, market). The flat SEO-rules content-authoring shape: a
        subject + a PLAIN-text body with {{variables}} (NOT the CMS rich-text editor),
        an "available variables" helper from the const contract, and a soft
        subset-validation warning on an undeclared placeholder. Gated
        email_templates:write (editor and super_admin), automatic audit, no forced
        reason; both-market via the S1 CrossRegionConfirm. */}
    <Resource
      name="emailTemplates"
      list={EmailTemplateList}
      edit={EmailTemplateEdit}
    />
    {/* Earnings is an analytical PAGE, not a CRUD resource, so it mounts as a
        custom route under Reports (read-only; D-earnings). */}
    <CustomRoutes>
      <Route path="/earnings" element={<Earnings />} />
      <Route path="/store-performance" element={<StorePerformance />} />
      <Route path="/exit-clicks" element={<ExitClicks />} />
      {/* Subscriptions is a filtered VIEW over users (not a Resource), so it
          mounts as a custom route. It MUTATES (two guarded actions), audited
          for free through the provider wrapper (D-subscriptions). */}
      <Route path="/subscriptions" element={<Subscriptions />} />
      {/* Referrals is a read-mostly filtered VIEW over users (not a Resource),
          with one guarded action (void), audited for free (D-referrals). */}
      <Route path="/referrals" element={<Referrals />} />
      {/* Manual attribution is a virtual review queue over orphan transactions
          (not a Resource), with three guarded actions (credit / reject / flag)
          through the chokepoint, audited for free. A standalone ops surface taken
          ahead of Phase 5 (D-manual-attribution). */}
      <Route path="/manual-attribution" element={<ManualAttribution />} />
      {/* Fraud queue (Phase 5 lead): a ranked virtual view over flagged users
          (not a Resource), with guarded recorded-decision actions through the
          chokepoint, audited for free. Role-gated to fraud / super_admin and
          investigative-global, not switcher-scoped (D-fraud-queue). */}
      <Route path="/fraud-queue" element={<FraudQueue />} />
      {/* Suspensions (Phase 5, surface 2): the suspension LIFECYCLE worklist over
          held users (not a Resource). Structured reason codes, an admin-chosen
          period, a derived elapsed-pending-lift state and a recorded appeal path,
          with reinstate / overturn / dismiss through the chokepoint, audited for
          free. Role-gated to fraud / super_admin and investigative-global
          (D-suspension-flow). */}
      <Route path="/suspensions" element={<Suspensions />} />
      {/* Ban evasion (Phase 5, surface 3): a relationship/detection surface over
          users (not a Resource). Derives suspected same-person links anchored on
          banned accounts by matching modelled identity signals plus the referral
          cluster, with a value-neutral dismiss and the inherited ban through the
          chokepoint, audited for free. Role-gated to fraud / super_admin and
          investigative-global with a cross-market match (D-ban-evasion). */}
      <Route path="/ban-evasion" element={<BanEvasion />} />
      {/* Chargebacks (Phase 5, surface 4): the disputed-transaction lifecycle over
          transactions (not a Resource). A dispute is orthogonal to settlement
          status; resolving lost claws back the cashback as a negative ledger entry
          (the reversal deriveBalance subtracts), never a direct balance write.
          Trust is asymmetric by outcome (won = fraud, lost = super_admin).
          Operational and switcher-scoped, role-gated to fraud / super_admin
          (D-chargebacks). */}
      <Route path="/chargebacks" element={<Chargebacks />} />
      {/* Manual balance adjustments (Phase 5, surface 5, the capstone that CLOSES
          Phase 5): a read-only oversight ledger over the manual_adjustment entries
          (not a Resource). The adjustment itself is taken from the user detail and is
          a signed ledger-entry CREATE the balance derives from, never a direct balance
          write. super_admin-only (the highest-trust write), operational and switcher-
          scoped (D-balance-adjustment). */}
      <Route path="/adjustments" element={<Adjustments />} />
      {/* Ticket inbox (Phase 6 LEAD): a worklist over the first-class tickets
          collection (not a Resource), carrying the phase foundation (the support-agent
          role and the bounded support credit). Ticket actions and the support credit
          route through the chokepoint and the audit floor. Operational and switcher-
          scoped, role-gated to support / super_admin (D-ticket-inbox). */}
      <Route path="/tickets" element={<TicketInbox />} />
      {/* Tracking diagnostics (Phase 6, surface 3): a STRICTLY READ-ONLY per-member
          trace of the click-to-transaction chain, so support can debug a missing or
          wrong transaction fast. Keyed by member (with an optional ?transaction= focus),
          so it is reached from a member context (UserShow), not a standalone menu entry.
          Operational and switcher-scoped, role-gated to support / super_admin. No action,
          no chokepoint, no mutation (D-tracking-diagnostics). */}
      <Route
        path="/tracking-diagnostics/:userId"
        element={<TrackingDiagnostics />}
      />
      {/* View as user (Phase 6, surface 4, the close): a VIEW-ONLY member's-eye lens
          over a subject's data while a recorded, audited, time-bound session is active.
          The only writes are the session start and explicit end; the view writes nothing
          and never acts as the member. Keyed by member, reached from a "View as user"
          link on UserShow. Operational and switcher-scoped, role-gated to support /
          super_admin (D-impersonation). */}
      <Route path="/view-as/:userId" element={<ViewAsUser />} />
      {/* Data subject requests (Phase 7 LEAD): a compliance worklist over the first-class
          dsarRequests collection (not a Resource), carrying the phase foundation (the
          compliance role and the regulatory-SLA shape). The statutory deadline is derived off
          the clock; fulfilling assembles the member's own data package and records the request
          fulfilled through the chokepoint, rejecting captures a mandatory reason, all audited.
          Oversight-global with a region facet, role-gated to compliance / super_admin (D-dsar). */}
      <Route path="/dsar" element={<DsarQueue />} />
      {/* Consent records (Phase 7, surface 2): a per-member assembled-facts READ view of a
          member's append-only consent history and the derived standing consent per type (not a
          Resource, not a worklist). Read-only except the one narrow consent:record_withdrawal
          write (a compliance-logged off-app withdrawal append), audited. Keyed by member,
          reached from a "Consent records" link on UserShow. Oversight-global (not switcher-
          scoped), role-gated to compliance / super_admin (D-consent-records). */}
      <Route path="/consent/:userId" element={<ConsentRecords />} />
      {/* Sanctions screening (Phase 7, surface 3): a compliance review worklist over the
          engine-written sanctionsMatches collection (not a Resource), the DSAR / chargebacks
          worklist seam. Members are screened by the engine against the sanctions lists; the admin
          reviews the matches, clearing a false positive or confirming a true match through the
          chokepoint with a mandatory reason, audited. Confirming records a sanctions status (the
          member's standing is derived), does NOT flip fraudStatus, and the downstream enforcement
          is a cross-repo seam. Oversight-global with a region facet, role-gated to compliance /
          super_admin (D-sanctions). */}
      <Route path="/sanctions" element={<SanctionsQueue />} />
      {/* Tax reporting / 1099-K (Phase 7, surface 4): a PURE DERIVED READ over paid US payouts (not
          a Resource, no stored collection). Computes which US members crossed the IRS reporting
          threshold for a tax year, the deriveBalance filter-and-reduce. US-ONLY by construction
          (the first single-jurisdiction compliance surface, the inverse of the oversight-global
          posture), most-recent-complete tax year by default with a selector. No write, no filing
          machinery: a real 1099-K generation and filing is the migration seam. Role-gated to
          compliance / super_admin (D-tax-reporting). */}
      <Route path="/tax" element={<TaxReport />} />
      {/* Erasure / right-to-be-forgotten (Phase 7, surface 6, the last and sharpest): a compliance
          worklist over the erasureRequests collection (not a Resource). Centers on the
          disposition-plan PREVIEW (the blast radius shown before the irreversible execute). The
          lifecycle requested -> grace[derived off the clock, cancellable] -> executed | cancelled |
          rejected. Execute is a recorded decision plus a minimal owned-row TOMBSTONE (scrub the User
          PII, sever the two nullable links), NEVER a real cascade (that is the backend seam). Trust is
          SPLIT: request / cancel / reject at compliance, the irreversible execute super_admin-only
          (the maker-checker). Oversight-global with a region facet, role-gated to compliance /
          super_admin (D-erasure). */}
      <Route path="/erasure" element={<ErasureQueue />} />
      {/* Settings (Phase 9, S1, D-settings-s1): the SINGLETON-CONFIG foundation. A
          settings PAGE (not a Resource): the current region's system config (site name /
          currency / cashback default) as an edit form, the feature flags as a per-flag
          toggle list, and the Plus marketing flag as a state selector. Config is ONE ROW
          PER MARKET; a GLOBAL admin applies the low-stakes site details to both regions
          (two audited writes behind a region-naming confirm). The trust map: site name /
          currency settings:write (automatic audit), cashback default settings:configure
          (forced reason), flags feature_flags:write (forced reason), the Plus marketing
          flag marketing_flags:write SUPER_ADMIN-only (forced reason). The page gates on
          settings:write (editor and super_admin); the marketing control gates within. */}
      <Route path="/settings" element={<SettingsPage />} />
      {/* API docs (Phase 9, S1, D-settings-s1): a static internal reference of the admin
          API route groups. Read-only, no model, no mutation. Gated settings:read (any
          admin read; viewer via the read-everywhere '*:read'). */}
      <Route path="/api-docs" element={<ApiDocs />} />
      {/* US compliance settings (Phase 9, Settings S4, D-settings-s4-us-compliance): the LAST
          and HIGHEST-CONSEQUENCE Settings surface, which CLOSES Settings. A config PAGE (not a
          Resource): the 1099-K reporting thresholds (one row per year, the LIVE tax-report
          consumer, edited here and READ by the tax report as its crossing gate), the per-state
          privacy-regime toggles (a single enabled boolean per state), and the privacy / terms
          REFERENCES (slug pointers into the CMS legal pages, soft-validated, not a document
          store). US-ONLY (fixed market 'us', no cross-region apply-both). Every change forces a
          reason. ROLE-gated to compliance and super_admin (the tax-report precedent), the
          tightest hand in Settings: compliance OWNS regulatory config, editor and viewer are
          denied. The write affordances additionally check compliance_settings:write. */}
      <Route path="/compliance-settings" element={<ComplianceSettingsPage />} />
      {/* Internal alerts config (Phase 9, D-internal-alerts, the last feature surface): the CONFIG
          half. A config PAGE (not a Resource): per alert-type thresholds (the monetary types
          region-keyed, a both-market change behind CrossRegionConfirm; the count / none types
          global), the channel routing, and the global channel config (the write-only redacted
          Slack webhook + email routing). super_admin-only (alerts:write): a mis-set threshold is
          consequential operational config. Threshold and webhook changes force a reason. The admin
          CONFIGURES here; the backend fires and delivers (the seam), so there is no send-test
          affordance. The fired-alert trail (firedAlerts Resource) is read-gated more broadly. */}
      <Route path="/alert-config" element={<AlertConfigPage />} />
      {/* Rate rule calendar (Phase 8, surface 2, D-rate-rules): a scheduling DISPLAY
          over the derived lifecycle states (live / scheduled / draft / paused /
          expired) of the rules in scope. It computes nothing the resolver does not.
          Role-gated to editor and super_admin, operational market-scoped. */}
      <Route path="/rate-rule-calendar" element={<RuleCalendar />} />
    </CustomRoutes>
    {/* Events explorer (Phase 9, D-events-explorer): a READ-ONLY debug / search tool over the
        events stream for support and engineering. A Resource (list + show): the list reuses the
        AuditList search-filter skeleton (q + event-type / user / From-To facets, occurredAt DESC)
        and adds the first CSV export in the panel; the show is the full JSONB property-bag detail.
        Region-scoped by the switcher (the materialized market). ROLE-gated to support, super_admin
        and fraud (the debug audience): the role gate holds the boundary against a viewer's '*:read'
        (the AuditList precedent). Read-only, no mutation; the real event stream is the seam. */}
    <Resource name="events" list={EventList} show={EventShow} />
    {/* Fired-alert trail (Phase 9, D-internal-alerts): the read-only TRAIL half, a Resource
        (list + show) in the events-explorer shape. The backend writes it; the admin views the
        fired history. Role-gated to super_admin, fraud and support (broad read, the operational
        responders); the config that WRITES the thresholds is super_admin-only (the split). */}
    <Resource name="firedAlerts" list={AlertList} show={AlertShow} />
    <Resource name="audit" list={AuditList} />
  </Admin>
);

export default App;
